// BL-177 Step 3: scheduled send. THE rules, in one pure module that the browser (composer, schedule panel, bubbles) and the
// Deno Edge Functions (staff-schedule-message, scheduled-send-run) both import, so what the panel promises and what the scheduler
// does cannot drift apart. No dependencies: all time-zone work uses Intl.
//
// Times are stored as UTC instants (timestamptz). People pick and see them in the time zone chosen in the schedule panel
// (it starts as the COMPANY time zone, The Ride = America/New_York), with the recipient's local time beside it when it differs.
//
// BL-177 Step 2j: a staff message goes out at the time the person picked: there is NO quiet-hours hold for staff messages (one-to-one
// or group, any channel). The quiet-hours helpers further down (normalizeQuietHours, nextAllowedSendTime, clockLabel) are kept for the
// future SEO / Marketing module: quiet hours will apply only to Campaigns / marketing sends there, never to staff messages.

export const MIN_LEAD_MINUTES = 1;
export const MAX_LEAD_DAYS = 90;
export const WHATSAPP_WINDOW_HOURS = 24;
export const QUIET_HOURS_DEFAULT = Object.freeze(/** @type {{ start: string, end: string }} */ ({ start: '08:00', end: '21:00' }));
export const SCHEDULED_STATUSES = ['scheduled', 'sending', 'sent', 'failed', 'cancelled'];
export const SCHEDULED_CHANNELS = ['sms', 'mms', 'whatsapp', 'email'];

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// ---------------------------------------------------------------------------------------------
// Time-zone arithmetic (Intl only)
// ---------------------------------------------------------------------------------------------

const formatterCache = new Map();
function formatter(timeZone) {
    if (!formatterCache.has(timeZone)) {
        formatterCache.set(timeZone, new Intl.DateTimeFormat('en-US', {
            timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short',
        }));
    }
    return formatterCache.get(timeZone);
}

const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The wall-clock parts of an instant in a time zone: { year, month(1-12), day, hour, minute, second, weekday(0=Sun) }. */
export function zonedParts(instant, timeZone) {
    const parts = {};
    formatter(timeZone).formatToParts(new Date(instant)).forEach((part) => { parts[part.type] = part.value; });
    return {
        year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
        hour: Number(parts.hour) % 24, minute: Number(parts.minute), second: Number(parts.second), weekday: WEEKDAYS[parts.weekday],
    };
}

/** Offset of a time zone from UTC at an instant, in minutes (New York in summer = -240). */
export function zoneOffsetMinutes(instant, timeZone) {
    const p = zonedParts(instant, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return Math.round((asUtc - Math.floor(new Date(instant).getTime() / 1000) * 1000) / MINUTE);
}

/**
 * The instant at which a time zone's wall clock reads { year, month, day, hour, minute }.
 * Across daylight-saving changes: a time that does not exist (the spring-forward hour) moves forward to the first real
 * moment after the gap; a time that happens twice (the fall-back hour) is the FIRST occurrence.
 */
export function zonedTimeToInstant({ year, month, day, hour = 0, minute = 0 }, timeZone) {
    const wall = Date.UTC(year, month - 1, day, hour, minute, 0);
    // two passes settle the offset around a change
    const guess1 = wall - zoneOffsetMinutes(wall, timeZone) * MINUTE;
    const guess2 = wall - zoneOffsetMinutes(guess1, timeZone) * MINUTE;
    const candidates = [guess1, guess2].filter((value) => {
        const p = zonedParts(value, timeZone);
        return p.year === year && p.month === month && p.day === day && p.hour === hour && p.minute === minute;
    });
    if (candidates.length) return new Date(Math.min(...candidates));
    // the wall time falls in a spring-forward gap: use the instant just after the gap
    return new Date(Math.max(guess1, guess2));
}

function addDays({ year, month, day }, days) {
    const moved = new Date(Date.UTC(year, month - 1, day + days));
    return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() };
}

/** Parse "HH:MM" (also "H:MM", "HH:MM:SS") into { hour, minute }, or null. */
export function parseClock(value) {
    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value || '').trim());
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return hour <= 23 && minute <= 59 ? { hour, minute } : null;
}

// ---------------------------------------------------------------------------------------------
// The schedule panel's starting point and calendar range
// ---------------------------------------------------------------------------------------------

/** True when Intl knows the IANA time zone (a contact's saved zone could be anything). */
export function isValidTimeZone(timeZone) {
    if (!timeZone || typeof timeZone !== 'string') return false;
    try { new Intl.DateTimeFormat('en-US', { timeZone }).format(0); return true; } catch { return false; }
}

/** The first quarter-hour (:00 :15 :30 :45) that is at least the minimum lead ahead: 7:47 PM -> 8:00 PM, 7:59:40 PM -> 8:15 PM. */
export function nextQuarterHour(now = new Date()) {
    const quarter = 15 * MINUTE;
    return new Date(Math.ceil((new Date(now).getTime() + MIN_LEAD_MINUTES * MINUTE) / quarter) * quarter);
}

const pad2 = (value) => String(value).padStart(2, '0');

/** { year, month, day } -> "YYYY-MM-DD" */
export function dateKey({ year, month, day }) {
    return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

/** "YYYY-MM-DD" -> { year, month, day }, or null when it is not a real calendar date. */
export function parseDateKey(key) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || '').trim());
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const check = new Date(Date.UTC(year, month - 1, day));
    return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day ? { year, month, day } : null;
}

/** A calendar date moved by whole days ("2026-10-31" + 1 = "2026-11-01"). */
export function addDaysToDateKey(key, days) {
    const parts = parseDateKey(key);
    return parts ? dateKey(addDays(parts, days)) : '';
}

/**
 * The days the calendar allows, in a time zone: from today (the zone's today) to the day the 90-day limit falls on.
 * A day inside the range can still hold a time that is too early (today, earlier than now) or too late (the last day): the panel says so.
 */
export function scheduleDayRange(now = new Date(), timeZone = 'America/New_York') {
    const current = new Date(now).getTime();
    return {
        first: dateKey(zonedParts(current, timeZone)),
        last: dateKey(zonedParts(current + MAX_LEAD_DAYS * DAY, timeZone)),
    };
}

const US_ZONE_NAMES = [
    ['America/New_York', 'Eastern'], ['America/Chicago', 'Central'], ['America/Denver', 'Mountain'], ['America/Phoenix', 'Arizona'],
    ['America/Los_Angeles', 'Pacific'], ['America/Anchorage', 'Alaska'], ['Pacific/Honolulu', 'Hawaii'],
];

/** "Eastern (ET)", "Arizona (MST)", "Toronto (ET)", "London (GMT+1)": a plain name for a time zone at an instant. */
export function zoneDisplayName(timeZone, instant = new Date()) {
    const known = US_ZONE_NAMES.find(([zone]) => zone === timeZone);
    const name = known ? known[1] : String(timeZone || '').split('/').pop().replace(/_/g, ' ');
    return `${name} (${zoneAbbreviation(instant, timeZone)})`;
}

/**
 * The time zone choices in the schedule panel: the common US zones, plus the company's zone and the contact's zone when they are
 * somewhere else. Each is { value, label }; the company's and the contact's carry a note.
 */
export function scheduleZoneChoices({ companyTimeZone = 'America/New_York', recipientTimeZone = null, now = new Date() } = {}) {
    const zones = US_ZONE_NAMES.map(([zone]) => zone);
    [companyTimeZone, recipientTimeZone].forEach((zone) => { if (zone && !zones.includes(zone)) zones.push(zone); });
    return zones.map((zone) => {
        const notes = [zone === companyTimeZone ? 'company' : '', zone === recipientTimeZone ? 'recipient' : ''].filter(Boolean);
        return { value: zone, label: `${zoneDisplayName(zone, now)}${notes.length ? ` \u00B7 ${notes.join(' & ')}` : ''}` };
    });
}

/** From the date and time boxes (company time zone) to an instant, or null when they are incomplete / not a real date. */
export function pickedToInstant(dateText, timeText, timeZone = 'America/New_York') {
    const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateText || '').trim());
    const clock = parseClock(timeText);
    if (!date || !clock) return null;
    const year = Number(date[1]);
    const month = Number(date[2]);
    const day = Number(date[3]);
    const check = new Date(Date.UTC(year, month - 1, day));
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null; // e.g. Feb 31
    return zonedTimeToInstant({ year, month, day, ...clock }, timeZone);
}

/** The date/time box values for an instant, in the company time zone (for editing). */
export function instantToPicked(instant, timeZone = 'America/New_York') {
    const p = zonedParts(instant, timeZone);
    const pad = (value) => String(value).padStart(2, '0');
    return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

// ---------------------------------------------------------------------------------------------
// Validation, in plain English
// ---------------------------------------------------------------------------------------------

/** At least 1 minute (60 seconds, inclusive) and at most 90 days (inclusive) ahead. Returns { ok: true } or { ok: false, error }. */
export function validateScheduledTime(at, now = new Date()) {
    const time = at instanceof Date ? at.getTime() : Date.parse(at);
    if (!Number.isFinite(time)) return { ok: false, error: 'Pick a date and time for this message.' };
    const lead = time - new Date(now).getTime();
    if (lead < MIN_LEAD_MINUTES * MINUTE) {
        return { ok: false, error: lead < 0 ? 'That time has already passed. Pick a time in the future.' : `Pick a time at least ${MIN_LEAD_MINUTES} minute${MIN_LEAD_MINUTES === 1 ? '' : 's'} from now. To send right away, use Send.` };
    }
    if (lead > MAX_LEAD_DAYS * DAY) return { ok: false, error: `Messages can be scheduled up to ${MAX_LEAD_DAYS} days ahead. Pick an earlier date.` };
    return { ok: true };
}

// ---------------------------------------------------------------------------------------------
// Showing times
// ---------------------------------------------------------------------------------------------

const ZONE_ABBR_FALLBACK = { 'America/New_York': 'ET', 'America/Chicago': 'CT', 'America/Denver': 'MT', 'America/Phoenix': 'MST', 'America/Los_Angeles': 'PT', 'America/Anchorage': 'AKT', 'Pacific/Honolulu': 'HT' };

/** "ET", "CT", "GMT+1"... the short name of a zone at an instant. */
export function zoneAbbreviation(instant, timeZone) {
    try {
        const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date(instant)).find((part) => part.type === 'timeZoneName')?.value || '';
        if (timeZone === 'America/Phoenix') return 'MST'; // Arizona does not change clocks: it is Mountain STANDARD time all year, not "MT"
        const us = { EST: 'ET', EDT: 'ET', CST: 'CT', CDT: 'CT', MST: 'MT', MDT: 'MT', PST: 'PT', PDT: 'PT', AKST: 'AKT', AKDT: 'AKT', HST: 'HT' };
        return us[name] || name || ZONE_ABBR_FALLBACK[timeZone] || timeZone;
    } catch {
        return ZONE_ABBR_FALLBACK[timeZone] || timeZone;
    }
}

// Newer ICU versions put a narrow no-break space before AM/PM; people (and tests) expect a plain space.
const plain = (text) => text.replace(/[\u202f\u00a0]/g, ' ');

/** "Tue Oct 14, 8:00 AM ET" */
export function formatScheduledWhen(instant, timeZone = 'America/New_York') {
    const date = new Date(instant);
    const day = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', month: 'short', day: 'numeric' }).format(date);
    const time = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(date);
    return plain(`${day.replace(',', '')}, ${time} ${zoneAbbreviation(date, timeZone)}`);
}

/** "5:00 AM PT" (the recipient's clock), or '' when we do not know their zone or it equals the company's zone offset. */
export function recipientLocalLabel(instant, recipientTimeZone, companyTimeZone = 'America/New_York') {
    if (!recipientTimeZone || recipientTimeZone === companyTimeZone) return '';
    if (zoneOffsetMinutes(instant, recipientTimeZone) === zoneOffsetMinutes(instant, companyTimeZone)) return '';
    const date = new Date(instant);
    const time = new Intl.DateTimeFormat('en-US', { timeZone: recipientTimeZone, hour: 'numeric', minute: '2-digit' }).format(date);
    return plain(`${time} ${zoneAbbreviation(date, recipientTimeZone)}`);
}

// ---------------------------------------------------------------------------------------------
// The recipient's time zone, from the phone number (country and, in North America, the area code)
// ---------------------------------------------------------------------------------------------

const NANP_ZONES = {
    'America/New_York': '201 202 203 207 212 215 216 220 223 234 239 240 248 267 270? 272 276 301 302 305 321 326 327 330 332 334? 339 345? 347 351 352 380 386 404 407 410 412 419 423 434 440 443 445 470 475 478 484 513 516 517 518 540 551 561 567 570 571 574? 585 586 603 607 609 610 614 616 617 631 640 646 656 667 678 679 681 689 703 704 706 716 717 718 724 727 732 740 754 757 762 765? 770 772 774 781 786 802 803 804 810 813 814 828 835 838 844? 845 848 856 857 860 862 863 864 878 904 908 910 912 914 917 929 931? 934 937 941 943 947 948 954 959 978 980 984'.split(' '),
    'America/Chicago': '205 210 214 217 218 219 224 225 228 251 254 256 262 281 309 312 314 315? 318 319 320 331 334 346 361 364? 409 414 430 432 469 479 501 504 507 512 515 531 563 573 601 608 612 615 618 620 630 636 641 651 660 662 682 708 712 713 715 726 731 737 763 769 773 779 785 806 815 817 830 832 847 870 872 903 913 915 918 920 925? 936 940 952 956 972 979 985'.split(' '),
    'America/Denver': '303 307 385 406 435 505 575 623? 719 720 801 970 983 208 986'.split(' '),
    'America/Phoenix': '480 520 602 623 928'.split(' '),
    'America/Los_Angeles': '206 209 213 253 279 310 323 341 360 408 415 424 425 442 458 503 509 510 530 541 559 562 619 626 628 650 657 661 669 702 707 714 725 747 760 775 805 818 831 858 909 916 925 949 951 971'.split(' '),
    'America/Anchorage': ['907'],
    'Pacific/Honolulu': ['808'],
    'America/Puerto_Rico': '787 939'.split(' '),
    'America/Toronto': '226 249 289 343 365 416 437 519 548 613 647 705 807 905'.split(' '),
    'America/Montreal': '263 367 418 438 450 514 579 581 819 873'.split(' '),
    'America/Halifax': '506 782 902 428 879'.split(' '),
    'America/St_Johns': ['709'],
    'America/Winnipeg': ['204', '431'],
    'America/Regina': ['306', '639'],
    'America/Edmonton': '403 587 780 825'.split(' '),
    'America/Vancouver': '236 250 604 672 778'.split(' '),
    'America/Jamaica': ['876', '658'],
    'America/Santo_Domingo': ['809', '829', '849'],
};
// A few codes in the lists above are uncertain across regions; a '?' marks them as "do not trust", so they resolve to unknown.
const NANP_BY_CODE = new Map();
Object.entries(NANP_ZONES).forEach(([zone, codes]) => codes.forEach((code) => { if (!code.endsWith('?')) NANP_BY_CODE.set(code, zone); }));

const COUNTRY_ZONES = {
    GB: 'Europe/London', IE: 'Europe/Dublin', DE: 'Europe/Berlin', FR: 'Europe/Paris', ES: 'Europe/Madrid', IT: 'Europe/Rome', NL: 'Europe/Amsterdam',
    BE: 'Europe/Brussels', CH: 'Europe/Zurich', AT: 'Europe/Vienna', PT: 'Europe/Lisbon', SE: 'Europe/Stockholm', NO: 'Europe/Oslo', DK: 'Europe/Copenhagen',
    FI: 'Europe/Helsinki', PL: 'Europe/Warsaw', GR: 'Europe/Athens', CZ: 'Europe/Prague', HU: 'Europe/Budapest', RO: 'Europe/Bucharest', TR: 'Europe/Istanbul',
    MX: 'America/Mexico_City', CO: 'America/Bogota', PE: 'America/Lima', AR: 'America/Argentina/Buenos_Aires', CL: 'America/Santiago', VE: 'America/Caracas',
    BR: 'America/Sao_Paulo', IN: 'Asia/Kolkata', PK: 'Asia/Karachi', BD: 'Asia/Dhaka', JP: 'Asia/Tokyo', KR: 'Asia/Seoul', CN: 'Asia/Shanghai', HK: 'Asia/Hong_Kong',
    SG: 'Asia/Singapore', PH: 'Asia/Manila', TH: 'Asia/Bangkok', VN: 'Asia/Ho_Chi_Minh', AE: 'Asia/Dubai', SA: 'Asia/Riyadh', IL: 'Asia/Jerusalem',
    EG: 'Africa/Cairo', NG: 'Africa/Lagos', GH: 'Africa/Accra', KE: 'Africa/Nairobi', ZA: 'Africa/Johannesburg', NZ: 'Pacific/Auckland',
};

/** The IANA time zone of a phone number, or null when we cannot tell (unknown country, multi-zone country without a known area). */
export function recipientTimeZoneForPhone(e164) {
    const digits = String(e164 || '').replace(/^whatsapp:/i, '').replace(/[^\d+]/g, '');
    if (!digits.startsWith('+') || digits.length < 8) return null;
    if (digits.startsWith('+1') && digits.length === 12) return NANP_BY_CODE.get(digits.slice(2, 5)) || null;
    const callingCodes = [['+44', 'GB'], ['+353', 'IE'], ['+49', 'DE'], ['+33', 'FR'], ['+34', 'ES'], ['+39', 'IT'], ['+31', 'NL'], ['+32', 'BE'], ['+41', 'CH'], ['+43', 'AT'],
        ['+351', 'PT'], ['+46', 'SE'], ['+47', 'NO'], ['+45', 'DK'], ['+358', 'FI'], ['+48', 'PL'], ['+30', 'GR'], ['+420', 'CZ'], ['+36', 'HU'], ['+40', 'RO'],
        ['+90', 'TR'], ['+52', 'MX'], ['+57', 'CO'], ['+51', 'PE'], ['+54', 'AR'], ['+56', 'CL'], ['+58', 'VE'], ['+55', 'BR'], ['+91', 'IN'], ['+92', 'PK'],
        ['+880', 'BD'], ['+81', 'JP'], ['+82', 'KR'], ['+86', 'CN'], ['+852', 'HK'], ['+65', 'SG'], ['+63', 'PH'], ['+66', 'TH'], ['+84', 'VN'], ['+971', 'AE'],
        ['+966', 'SA'], ['+972', 'IL'], ['+20', 'EG'], ['+234', 'NG'], ['+233', 'GH'], ['+254', 'KE'], ['+27', 'ZA'], ['+64', 'NZ']];
    const match = callingCodes.filter(([code]) => digits.startsWith(code)).sort((a, b) => b[0].length - a[0].length)[0];
    return match ? COUNTRY_ZONES[match[1]] || null : null;
}

// ---------------------------------------------------------------------------------------------
// Recipient quiet hours (default 8:00 AM - 9:00 PM in the RECIPIENT's local time)
//
// NOT USED for staff messages any more (BL-177 Step 2j: a staff message goes out when it is due). Kept for the future SEO / Marketing
// module, where quiet hours will apply only to Campaigns / marketing sends.
// ---------------------------------------------------------------------------------------------

export function normalizeQuietHours(value) {
    const start = parseClock(value && value.start);
    const end = parseClock(value && value.end);
    if (!start || !end) return { ...QUIET_HOURS_DEFAULT };
    const pad = (n) => String(n).padStart(2, '0');
    const result = { start: `${pad(start.hour)}:${pad(start.minute)}`, end: `${pad(end.hour)}:${pad(end.minute)}` };
    // an "allowed" window must be a real daytime window (start before end); anything else falls back to the default
    return start.hour * 60 + start.minute < end.hour * 60 + end.minute ? result : { ...QUIET_HOURS_DEFAULT };
}

/**
 * When a message due at `due` may really go out, given the recipient's allowed window. Inside the window it is `due` itself;
 * outside, the next moment the window opens (the start time, recipient local). With no known recipient zone the company zone is used.
 * Returns { at, waited: boolean, recipientZone }.
 * @param {Date | string | number} due
 * @param {{ recipientTimeZone?: string | null, companyTimeZone?: string, quiet?: { start: string, end: string } | null }} [options]
 * @returns {{ at: Date, waited: boolean, recipientZone: string }}
 */
export function nextAllowedSendTime(due, { recipientTimeZone = null, companyTimeZone = 'America/New_York', quiet = QUIET_HOURS_DEFAULT } = {}) {
    const zone = recipientTimeZone || companyTimeZone;
    const window = normalizeQuietHours(quiet);
    const start = parseClock(window.start);
    const end = parseClock(window.end);
    const instant = new Date(due);
    const p = zonedParts(instant, zone);
    const minutes = p.hour * 60 + p.minute;
    const startMinutes = start.hour * 60 + start.minute;
    const endMinutes = end.hour * 60 + end.minute;
    if (minutes >= startMinutes && minutes < endMinutes) return { at: instant, waited: false, recipientZone: zone };
    const day = minutes < startMinutes ? { year: p.year, month: p.month, day: p.day } : addDays(p, 1);
    return { at: zonedTimeToInstant({ ...day, hour: start.hour, minute: start.minute }, zone), waited: true, recipientZone: zone };
}

/** "9:00 PM" style label of a window edge, for messages. */
export function clockLabel(value) {
    const clock = parseClock(value);
    if (!clock) return String(value || '');
    const suffix = clock.hour >= 12 ? 'PM' : 'AM';
    return `${((clock.hour + 11) % 12) + 1}:${String(clock.minute).padStart(2, '0')} ${suffix}`;
}

// ---------------------------------------------------------------------------------------------
// WhatsApp's 24-hour window
// ---------------------------------------------------------------------------------------------

/** The most recent inbound message time in a conversation log (ISO string), or null. */
export function lastInboundAt(rawMessageLog) {
    const log = Array.isArray(rawMessageLog) ? rawMessageLog : [];
    let latest = null;
    log.forEach((entry) => {
        if (!entry || entry.direction !== 'inbound') return;
        const time = Date.parse(entry.received_at || entry.at || entry.timestamp || entry.created_at || '');
        if (Number.isFinite(time) && (latest === null || time > latest)) latest = time;
    });
    return latest === null ? null : new Date(latest).toISOString();
}

/** Free-form WhatsApp is allowed only within 24 h of the customer's last message, measured at the time the message will GO OUT. */
export function whatsappWindowOpenAt(lastInbound, sendAt) {
    const inbound = lastInbound ? Date.parse(lastInbound) : NaN;
    if (!Number.isFinite(inbound)) return false;
    return new Date(sendAt).getTime() - inbound <= WHATSAPP_WINDOW_HOURS * HOUR;
}

export const WHATSAPP_WINDOW_MESSAGE = 'WhatsApp only lets you send free-form messages within 24 hours of the customer\'s last message, and that window will be closed by then. Choose an approved message template instead, or pick an earlier time.';

// ---------------------------------------------------------------------------------------------
// Cancel-if-they-reply
// ---------------------------------------------------------------------------------------------

/** True when the customer wrote after the message was scheduled (so a "cancel if they reply" message should not go out). */
export function repliedSince(rawMessageLog, sinceIso) {
    const since = Date.parse(sinceIso || '');
    const latest = lastInboundAt(rawMessageLog);
    return Number.isFinite(since) && latest !== null && Date.parse(latest) > since;
}

// ---------------------------------------------------------------------------------------------
// Who may see / change a scheduled message
// ---------------------------------------------------------------------------------------------

/** Staff see and change their own; company admins and platform admins see and can cancel everyone's in their company. */
export function canManageScheduled(row, { userId, isAdmin }) {
    if (!row) return false;
    return Boolean(isAdmin) || (Boolean(userId) && row.created_by === userId);
}

/** Edit and Send now stay with the person who scheduled it; admins can cancel (and see) but not rewrite someone else's text. */
export function canEditScheduled(row, { userId }) {
    return Boolean(row) && Boolean(userId) && row.created_by === userId && row.status === 'scheduled';
}

export function describeScheduledStatus(row, timeZone = 'America/New_York') {
    if (!row) return '';
    const when = row.scheduled_for ? formatScheduledWhen(row.scheduled_for, timeZone) : '';
    if (row.status === 'scheduled') {
        if (row.waiting_until && Date.parse(row.waiting_until) > Date.parse(row.scheduled_for)) {
            return `Waiting until ${formatScheduledWhen(row.waiting_until, timeZone)}: ${row.status_reason || 'outside the recipient\'s allowed hours'}`;
        }
        return `Scheduled for ${when}`;
    }
    if (row.status === 'sending') return 'Sending now';
    if (row.status === 'sent') return `Sent${row.sent_at ? ` ${formatScheduledWhen(row.sent_at, timeZone)}` : ''}`;
    if (row.status === 'cancelled') return row.status_reason || 'Cancelled';
    if (row.status === 'failed') return `Not sent: ${row.status_reason || 'the message could not be sent'}`;
    return '';
}
